/**
 * RiskFormat — shared formatting/lookup helpers for the route risk assessment UI (panel + overlay),
 * so bands, colours and wording come from one source. Pure functions, no DOM, no Google Maps.
 *
 * The band table is normally delivered by the backend (`bands` in the /routing/assess response);
 * DEFAULT_BANDS mirrors concept §5.5 and is only the fallback.
 */

/** Coverage factor below which a segment is drawn dashed and its value is greyed (§5.9 flag only). */
export const LOW_COVERAGE = 0.3;

export const DEFAULT_BANDS = [
  { name: "Low", min: 1, max: 20, color: "#28a745", textColor: "#ffffff" },
  { name: "Guarded", min: 21, max: 40, color: "#8bc34a", textColor: "#212529" },
  { name: "Elevated", min: 41, max: 60, color: "#ffc107", textColor: "#212529" },
  { name: "High", min: 61, max: 80, color: "#fd7e14", textColor: "#ffffff" },
  { name: "Severe", min: 81, max: 100, color: "#dc3545", textColor: "#ffffff" },
];

const FALLBACK_BAND = { name: "n/a", min: 0, max: 0, color: "#6c757d", textColor: "#ffffff" };

/**
 * Returns the band a score falls into. `bandName` is used as a secondary lookup when the score is
 * missing (or does not match any range) but the backend already named the band.
 *
 * @param {number|null} score 1..100
 * @param {Array<{name: string, min: number, max: number, color: string, textColor: string}>} [bands]
 * @param {string|null} [bandName]
 * @returns {{name: string, min: number, max: number, color: string, textColor: string}}
 */
export function bandFor(score, bands, bandName = null) {
  const table = Array.isArray(bands) && bands.length ? bands : DEFAULT_BANDS;
  const value = Number(score);
  if (Number.isFinite(value)) {
    const rounded = Math.round(value);
    const hit = table.find((b) => rounded >= b.min && rounded <= b.max);
    if (hit) {
      return hit;
    }
  }
  if (bandName) {
    const byName = table.find((b) => b.name === bandName);
    if (byName) {
      return byName;
    }
  }
  return FALLBACK_BAND;
}

/**
 * Inline style string for a band badge.
 * @param {{color: string, textColor: string}} band
 * @returns {string}
 */
export function bandStyle(band) {
  const b = band || FALLBACK_BAND;
  return `background-color:${b.color};color:${b.textColor || "#ffffff"};`;
}

/**
 * @param {*} value
 * @returns {string}
 */
export function escapeHtml(value) {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * @param {number|null} km
 * @param {number} [digits]
 * @returns {string}
 */
export function formatKm(km, digits = 0) {
  const value = Number(km);
  if (!Number.isFinite(value)) {
    return "n/a";
  }
  return `${value.toFixed(digits)} km`;
}

/**
 * @param {number|null} value
 * @param {number} [digits]
 * @returns {string}
 */
export function formatNumber(value, digits = 1) {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(digits) : "n/a";
}

/**
 * Renders a KPI value as HTML. Gated / inapplicable KPIs show "n/a" with the gate reason as tooltip.
 *
 * @param {{raw: number|null, applicable: boolean, gateReason: string|null}|null} kpi
 * @param {(raw: number) => string} formatter
 * @returns {string} HTML
 */
export function kpiText(kpi, formatter) {
  if (!kpi) {
    return `<span class="text-muted">n/a</span>`;
  }
  if (kpi.applicable === false) {
    const reason = kpi.gateReason ? ` title="${escapeHtml(kpi.gateReason)}"` : "";
    return `<span class="text-muted"${reason}>n/a</span>`;
  }
  if (kpi.raw === null || kpi.raw === undefined || !Number.isFinite(Number(kpi.raw))) {
    return `<span class="text-muted">n/a</span>`;
  }
  return escapeHtml(formatter(Number(kpi.raw)));
}

/**
 * Bootstrap alert class for a disclaimer severity.
 * @param {"permanent"|"hard"|"soft"} severity
 * @returns {string}
 */
export function disclaimerClass(severity) {
  switch (severity) {
    case "hard":
      return "alert alert-danger";
    case "soft":
      return "alert alert-warning";
    default:
      return "alert alert-secondary";
  }
}

/**
 * Human label for a mandatory-stop reason code.
 * @param {string} reason
 * @returns {string}
 */
export function reasonLabel(reason) {
  switch (reason) {
    case "continuous_driving_limit":
      return "45-min break";
    case "daily_rest":
      return "daily rest";
    case "planned":
      return "planned stop";
    default:
      return reason ? String(reason).replace(/_/g, " ") : "stop";
  }
}

/**
 * Numeric part of a segment id ("seg-3" → 3). Falls back to `fallback` when the id is not in that form.
 * @param {string} segmentId
 * @param {number} fallback
 * @returns {number}
 */
export function segmentNumber(segmentId, fallback) {
  const match = /(\d+)\s*$/.exec(String(segmentId || ""));
  return match ? Number(match[1]) : fallback;
}

/**
 * Human label for a coverage basis of the KPI object (§5.9 cascade step).
 * @param {string} basis
 * @returns {string}
 */
export function coverageBasisLabel(basis) {
  const labels = {
    reporter_diversity: "reporter diversity",
    facility_density: "facility density",
    volume_continuity: "reporting volume",
  };
  return labels[basis] || String(basis || "").replace(/_/g, " ");
}

/**
 * Human label for a KPI key of the KPI object.
 * @param {string} key
 * @returns {string}
 */
export function kpiLabel(key) {
  const labels = {
    incident_density: "Incident density",
    trend: "Trend",
    hotspot_concentration: "Hotspot concentration",
    secure_parking_coverage: "Secure parking coverage",
    mandatory_stop_safety: "Mandatory-stop safety",
    overnight_without_psr: "Overnight without PSR",
    border_crossings: "Border crossings",
    unsecured_parking_share: "Unsecured parking share",
    isolation_chokepoint: "Isolation / chokepoint",
    cargo_attractiveness: "Cargo attractiveness",
    observed_severity: "Observed severity",
  };
  return labels[key] || String(key || "").replace(/_/g, " ");
}

/**
 * "YYYY-MM-DDTHH:mm" (local wall clock from the engine) → "HH:mm", or with the day when
 * `withDate` is set ("Fri 08:00"). Returns "" for null/invalid input.
 * @param {string|null|undefined} local
 * @param {boolean} [withDate]
 * @returns {string}
 */
export function formatLocalTime(local, withDate = false) {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(String(local || ""));
  if (!match) {
    return "";
  }
  const time = `${match[4]}:${match[5]}`;
  if (!withDate) {
    return time;
  }
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  const day = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][date.getUTCDay()];
  return `${day} ${match[3]}.${match[2]}. ${time}`;
}
